import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const LEGAL_ENTITY = "Trajetória Lendária Unipessoal Lda";
const NIPC = "519689623";

const TITLES = {
  privacidade: "Política de Privacidade",
  "protecao-dados": "Proteção de Dados",
  cookies: "Política de Cookies",
  termos: "Termos e Condições de Utilização",
  "quem-somos": "Quem Somos",
};

// Map page key -> legal_docs key
const DOC_KEY = {
  privacidade: "privacidade",
  "protecao-dados": "protecao_dados",
  cookies: "cookies",
  termos: "termos",
  "quem-somos": "quem_somos",
};

function renderContent(text) {
  // Split into blocks by blank lines; lines starting with "## " become headings
  return (text || "")
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block, i) => {
      const lines = block.split("\n");
      return (
        <div key={i} className="mb-6">
          {lines.map((line, j) => {
            const t = line.trim();
            if (t.startsWith("## ")) return <h3 key={j} className="font-display text-lg text-[#0072C6] mt-4 mb-1">{t.slice(3)}</h3>;
            if (t.startsWith("# ")) return <h2 key={j} className="font-display text-xl text-[#0072C6] mt-5 mb-2">{t.slice(2)}</h2>;
            if (t.startsWith("- ")) return <li key={j} className="text-slate-600 text-sm leading-relaxed ml-5 list-disc">{t.slice(2)}</li>;
            return <p key={j} className="text-slate-600 text-sm leading-relaxed">{t}</p>;
          })}
        </div>
      );
    });
}

export default function LegalPage({ page }) {
  const settings = useSiteSettings();
  const title = TITLES[page];
  const docKey = DOC_KEY[page];
  const doc = (settings.legal_docs || {})[docKey];

  // Backwards compat: privacidade uses legacy privacy_policy_text if legal_docs empty
  let content = doc?.content || "";
  let status = doc?.status || "draft";
  let updatedAt = doc?.updated_at || "";
  if (page === "privacidade" && !content && settings.privacy_policy_text) {
    content = settings.privacy_policy_text;
    status = "published";
  }

  if (!title) return null;

  const isPublished = status === "published" && content.trim() !== "";

  return (
    <div className="min-h-screen bg-white" data-testid={`legal-${page}`}>
      <Header />
      <main className="pt-24 pb-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <h1 className="font-display text-4xl tracking-tight text-[#0072C6] mb-4">{title}</h1>
          <p className="text-xs text-slate-400 mb-10">
            {updatedAt
              ? `Última atualização: ${new Date(updatedAt).toLocaleDateString("pt-PT")}`
              : "Última atualização: —"}{" "}
            · {LEGAL_ENTITY} · NIPC {NIPC}
          </p>

          {isPublished ? (
            <div data-testid={`legal-content-${page}`}>{renderContent(content)}</div>
          ) : (
            <div className="bg-[#F8FAFC] border border-slate-200 p-10 text-center" data-testid={`legal-empty-${page}`}>
              <p className="text-slate-500 text-sm">
                Este documento ainda não foi publicado. Por favor, volte mais tarde.
              </p>
            </div>
          )}

          <p className="mt-12 text-sm text-slate-500 border-t border-slate-100 pt-6">
            Para questões sobre este documento, contacte-nos através da página de contactos.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
